import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { SelectControl } from '@/components/ui/Select'
import {
  ADVANCE_CURRENCIES,
  MARKETING,
  PROVIDERS,
  draftRateMode,
  expenseBody,
  rubPreview,
  withCampaign,
  withCategory,
  withChannel,
  withCurrency,
  type CampaignOption,
  type ExpenseDraft,
  type ExpenseField,
  type ExpenseMeta,
} from '@/lib/expenses'
import { moneyText } from '@/lib/finance'

export interface ChannelOption {
  id: number
  name: string
  is_active: boolean
}

export function emptyExpense(today: string): ExpenseDraft {
  return {
    spent_on: today,
    category: 'hosting',
    amount: '',
    currency: 'RUB',
    rate: '',
    note: '',
    channelId: '',
    campaignId: '',
    provider: '',
    receiptUrl: '',
  }
}

/**
 * «Бухгалтерия → Расходы»: a new expense. A marketing expense may name the acquisition channel
 * and campaign it paid for (the campaign sets its channel; the P&L still subtracts it once, under
 * «Маркетинг»). EUR has no rate feed, so its rate — rubles per 1 € — is typed and required;
 * USDT / USD take the USDT/RUB feed unless a rate is typed. The ruble amount the server will fix
 * is previewed exactly (amount × rate, kopecks). Numbers are typed the Russian way. Presentational;
 * the page posts the body. Every state in Storybook.
 *
 * «Пополнение провайдеров» is not an expense: it is recorded as an ADVANCE to the provider (owner
 * 2026-10-22) — the form asks which provider and takes USDT, USD or rubles (a ruble top-up becomes
 * USD at the USDT/RUB rate). Any row may carry a link to its receipt.
 */
export function ExpenseForm({
  today,
  categories,
  categoryLabel,
  currencies,
  meta,
  channels,
  campaigns,
  providers,
  busy = false,
  onSubmit,
  initial,
  showErrors = false,
}: {
  today: string
  categories: string[]
  categoryLabel: Record<string, string>
  currencies: string[]
  meta: ExpenseMeta
  /** acquisition channels (GET /acquisition/channels); undefined = not loaded / no access */
  channels: ChannelOption[] | undefined
  campaigns: CampaignOption[] | undefined
  /** advance providers: id → title (GET /accounting/expenses → advance_providers) */
  providers?: Record<string, string>
  busy?: boolean
  /** resolves true when the server took it (the form clears amount and note) */
  onSubmit: (body: Record<string, unknown>) => Promise<boolean> | void
  /** stories: typed values */
  initial?: Partial<ExpenseDraft>
  /** stories: check the typed values right away (the invalid state) */
  showErrors?: boolean
}) {
  const [draft, setDraft] = useState<ExpenseDraft>(() => ({ ...emptyExpense(today), ...initial }))
  const [errors, setErrors] = useState<Partial<Record<ExpenseField, string>>>(() => {
    if (!showErrors) return {}
    const res = expenseBody({ ...emptyExpense(today), ...initial }, meta, campaigns ?? [])
    return res.ok ? {} : res.errors
  })
  const mode = draftRateMode(draft, meta)
  const marketing = draft.category === MARKETING
  const advance = draft.category === PROVIDERS
  const shownCurrencies = advance ? currencies.filter((c) => ADVANCE_CURRENCIES.includes(c)) : currencies
  const shownCampaigns = (campaigns ?? []).filter((c) => !draft.channelId || String(c.channel_id) === draft.channelId)
  // A ruble top-up is already in rubles: its rate turns it into USD, not into ₽.
  const preview =
    mode === 'none' || (advance && draft.currency === 'RUB')
      ? null
      : rubPreview(draft.amount, draft.rate.trim() ? draft.rate : null)

  function update(next: ExpenseDraft, field?: ExpenseField) {
    setDraft(next)
    if (field) setErrors((e) => ({ ...e, [field]: undefined }))
  }

  async function submit() {
    const res = expenseBody(draft, meta, campaigns ?? [])
    if (!res.ok) {
      setErrors(res.errors)
      return
    }
    const ok = await onSubmit(res.body)
    if (ok) setDraft((d) => ({ ...d, amount: '', note: '', rate: '', receiptUrl: '' }))
  }

  return (
    <Card>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Box label="Дата" error={errors.spent_on}>
          <Input
            type="date"
            value={draft.spent_on}
            aria-invalid={errors.spent_on ? true : undefined}
            onChange={(e) => update({ ...draft, spent_on: e.target.value }, 'spent_on')}
          />
        </Box>
        <Box label="Категория">
          <SelectControl
            value={draft.category}
            onChange={(v) => {
              const next = withCategory(draft, v)
              // an advance is in USDT / USD / RUB: an EUR draft falls back to USDT
              const cur = v === PROVIDERS && !ADVANCE_CURRENCIES.includes(next.currency) ? 'USDT' : next.currency
              update(withCurrency(next, cur, meta), 'provider')
            }}
            options={categories.map((c) => ({ value: c, label: categoryLabel[c] ?? c }))}
          />
        </Box>
        <Box label="Сумма" error={errors.amount}>
          <Input
            className="tnum text-right"
            inputMode="decimal"
            value={draft.amount}
            placeholder="0,00"
            aria-invalid={errors.amount ? true : undefined}
            onChange={(e) => update({ ...draft, amount: e.target.value }, 'amount')}
          />
        </Box>
        <Box label="Валюта" error={errors.currency}>
          <SelectControl
            value={draft.currency}
            onChange={(v) => update(withCurrency(draft, v, meta), 'rate')}
            options={shownCurrencies.map((c) => ({ value: c, label: c }))}
          />
        </Box>
        {advance && (
          <Box label="Провайдер" error={errors.provider} hint="Пополнение — аванс провайдеру, не расход периода">
            <SelectControl
              value={draft.provider ?? ''}
              aria-invalid={errors.provider ? true : undefined}
              onChange={(v) => update({ ...draft, provider: v }, 'provider')}
              options={[
                { value: '', label: 'Выберите провайдера' },
                ...Object.entries(providers ?? {}).map(([id, title]) => ({ value: id, label: title })),
              ]}
            />
          </Box>
        )}
        {mode !== 'none' && (
          <Box
            label={`Курс, ₽ за 1 ${advance && draft.currency === 'RUB' ? 'USDT' : draft.currency}${mode === 'required' ? '' : ' (необязательно)'}`}
            error={errors.rate}
            hint={
              mode === 'required'
                ? 'Ленты курса нет — введите курс банка или карты, которой платили'
                : 'Пусто — курс USDT/RUB из ленты на момент записи'
            }
          >
            <Input
              className="tnum text-right"
              inputMode="decimal"
              value={draft.rate}
              placeholder={mode === 'required' ? 'например 100,25' : 'из ленты'}
              aria-invalid={errors.rate ? true : undefined}
              onChange={(e) => update({ ...draft, rate: e.target.value }, 'rate')}
            />
          </Box>
        )}
        {marketing && channels !== undefined && (
          <>
            <Box label="Канал привлечения" hint="Пусто — строка «Не распределено» в отчёте «Привлечение»">
              <SelectControl
                value={draft.channelId}
                onChange={(v) => update(withChannel(draft, v, campaigns ?? []), 'campaignId')}
                options={[
                  { value: '', label: 'Не указан' },
                  ...channels.map((c) => ({ value: String(c.id), label: `${c.name}${c.is_active ? '' : ' (выключен)'}` })),
                ]}
              />
            </Box>
            <Box label="Кампания" error={errors.campaignId} hint="Кампания сама задаёт канал">
              <SelectControl
                value={draft.campaignId}
                disabled={shownCampaigns.length === 0}
                aria-invalid={errors.campaignId ? true : undefined}
                onChange={(v) => update(withCampaign(draft, v, campaigns ?? []), 'campaignId')}
                options={[
                  { value: '', label: shownCampaigns.length === 0 ? 'Кампаний нет' : 'Без кампании' },
                  ...shownCampaigns.map((c) => ({ value: String(c.id), label: `${c.name} (${c.code})` })),
                ]}
              />
            </Box>
          </>
        )}
        <Box label="Комментарий">
          <Input value={draft.note} placeholder="за что" onChange={(e) => update({ ...draft, note: e.target.value })} />
        </Box>
        <Box label="Ссылка на чек (необязательно)" error={errors.receiptUrl}>
          <Input
            type="url"
            inputMode="url"
            value={draft.receiptUrl ?? ''}
            placeholder="https://"
            aria-invalid={errors.receiptUrl ? true : undefined}
            onChange={(e) => update({ ...draft, receiptUrl: e.target.value }, 'receiptUrl')}
          />
        </Box>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        {preview !== null && (
          <span className="tnum text-caption text-ink-2" aria-live="polite">
            В рублях будет {moneyText(preview)}{'\u00a0'}₽
          </span>
        )}
        {mode === 'optional' && preview === null && draft.amount.trim() !== '' && (
          <span className="text-caption text-ink-3">В рублях — по курсу из ленты при записи</span>
        )}
        <Button variant="primary" loading={busy} disabled={!draft.amount.trim()} onClick={() => void submit()}>
          {advance ? 'Записать пополнение' : 'Записать расход'}
        </Button>
      </div>
      {marketing && (
        <p className="mt-2 text-caption text-ink-3">
          Комиссии партнёрской программы расходом не вносятся: они уже считаются по начислениям.
        </p>
      )}
    </Card>
  )
}

function Box({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="mb-1.5 block text-caption text-ink-2">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-caption text-ink-3">{hint}</span>}
      {error && <span className="mt-1 block text-caption text-danger">✕ {error}</span>}
    </label>
  )
}
